import { Injectable } from '@angular/core'
import { New_TableOperator } from '@lla-platform/core/core-data-access'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { forkJoin, of } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'
import { ITireInventoryListItem } from '../../interfaces/tire-inventory.interface'
import {
  IUsafOrder,
  IUsafStockCheckBranch,
  IUsafStockCheckResult,
  IUsafSubmitOrderResponse,
  UsafStockCheckFailedError
} from '../../interfaces/usaf-order.interface'
import { TiresService } from '../tires.service'
import { UsafService } from '../usaf.service'
import {
  IUsafAutoOrderAttemptsListResponse,
  IUsafAutoOrderOverviewResponse,
  IUsafLeftoversListResponse,
  IUsafOverdueOrdersListResponse
} from '../../interfaces/usaf-auto-order.interface'
import {
  CheckUsafStock,
  GetUsafAutoOrderAttemptsList,
  GetUsafLeftoversList,
  GetUsafOverdueOrdersList,
  GetUsafWorkQueueSummary,
  GetUsafAutoOrderOverview,
  ClearUsafLocationData,
  ClearUsafStock,
  ClearUsafTireSuggestions,
  GetUsafInvoicesList,
  GetUsafOrderDetail,
  GetUsafRefillLocations,
  GetUsafRefillPlan,
  SubmitUsafRefill,
  GetUsafOrderHistory,
  GetUsafOrderLinesList,
  GetUsafOrdersList,
  GetUsafOrdersOverview,
  RefreshUsafOrderStatuses,
  SearchUsafTireSuggestions,
  SetUsafStocks,
  SubmitUsafOrder
} from './usaf.actions'
import {
  IUsafInvoicesListResponse,
  IUsafOrderLinesListResponse,
  IUsafOrdersListResponse,
  IUsafOrdersOverviewResponse
} from '../../interfaces/usaf-lists.interface'
import { UsafStateModel } from './usaf.model'
import {
  IUsafRefillLocation,
  IUsafRefillPlanResponse,
  IUsafRefillSubmitResult
} from '../../interfaces/usaf-refill.interface'

const SUGGESTIONS_MAX = 20

@State<UsafStateModel>({
  name: 'usaf',
  defaults: {
    orderHistory: undefined,
    stockByLineId: {},
    branches: [],
    lastSubmitResponse: undefined,
    tireSuggestions: [],
    ordersListResponse: undefined,
    ordersOverviewResponse: undefined,
    orderLinesListResponse: undefined,
    invoicesListResponse: undefined,
    autoOrderAttemptsListResponse: undefined,
    leftoversListResponse: undefined,
    overdueOrdersListResponse: undefined,
    leftoversSummary: undefined,
    overdueOrdersSummary: undefined,
    autoOrderOverviewResponse: undefined,
    orderDetail: undefined,
    refillLocations: undefined,
    refillPlans: {},
    refillSubmitResults: {}
  }
})
@Injectable()
export class UsafState {
  constructor(
    private usafService: UsafService,
    private tiresService: TiresService
  ) {}

  @Selector()
  static refillLocations(state: UsafStateModel): IUsafRefillLocation[] | undefined {
    return state.refillLocations
  }

  @Selector()
  static refillPlans(state: UsafStateModel): Record<string, IUsafRefillPlanResponse> {
    return state.refillPlans
  }

  @Selector()
  static refillSubmitResults(state: UsafStateModel): Record<string, IUsafRefillSubmitResult[]> {
    return state.refillSubmitResults
  }

  @Selector()
  static orderHistory(state: UsafStateModel): IUsafOrder[] | undefined {
    return state.orderHistory
  }

  @Selector()
  static stockByLineId(state: UsafStateModel): Record<string, IUsafStockCheckResult> {
    return state.stockByLineId
  }

  @Selector()
  static branches(state: UsafStateModel): IUsafStockCheckBranch[] {
    return state.branches
  }

  @Selector()
  static lastSubmitResponse(state: UsafStateModel): IUsafSubmitOrderResponse | undefined {
    return state.lastSubmitResponse
  }

  @Selector()
  static tireSuggestions(state: UsafStateModel): ITireInventoryListItem[] {
    return state.tireSuggestions
  }

  @Selector()
  static ordersListResponse(state: UsafStateModel): IUsafOrdersListResponse | undefined {
    return state.ordersListResponse
  }

  @Selector()
  static ordersOverviewResponse(state: UsafStateModel): IUsafOrdersOverviewResponse | undefined {
    return state.ordersOverviewResponse
  }

  @Selector()
  static orderLinesListResponse(state: UsafStateModel): IUsafOrderLinesListResponse | undefined {
    return state.orderLinesListResponse
  }

  @Selector()
  static invoicesListResponse(state: UsafStateModel): IUsafInvoicesListResponse | undefined {
    return state.invoicesListResponse
  }

  @Selector()
  static autoOrderAttemptsListResponse(
    state: UsafStateModel
  ): IUsafAutoOrderAttemptsListResponse | undefined {
    return state.autoOrderAttemptsListResponse
  }

  @Selector()
  static autoOrderOverviewResponse(state: UsafStateModel): IUsafAutoOrderOverviewResponse | undefined {
    return state.autoOrderOverviewResponse
  }

  @Selector()
  static leftoversListResponse(state: UsafStateModel): IUsafLeftoversListResponse | undefined {
    return state.leftoversListResponse
  }

  @Selector()
  static overdueOrdersListResponse(state: UsafStateModel): IUsafOverdueOrdersListResponse | undefined {
    return state.overdueOrdersListResponse
  }

  @Selector()
  static leftoversSummary(state: UsafStateModel): IUsafLeftoversListResponse | undefined {
    return state.leftoversSummary
  }

  @Selector()
  static overdueOrdersSummary(state: UsafStateModel): IUsafOverdueOrdersListResponse | undefined {
    return state.overdueOrdersSummary
  }

  @Selector()
  static orderDetail(state: UsafStateModel): IUsafOrder | undefined {
    return state.orderDetail
  }

  @Action(GetUsafOrderHistory, { cancelUncompleted: true })
  getOrderHistory(ctx: StateContext<UsafStateModel>, { locationId }: GetUsafOrderHistory) {
    ctx.patchState({ orderHistory: undefined })
    return this.usafService.getOrderHistory(locationId).pipe(
      tap((res) => {
        ctx.patchState({ orderHistory: res })
      })
    )
  }

  @Action(RefreshUsafOrderStatuses)
  refreshOrderStatuses(ctx: StateContext<UsafStateModel>, { locationId }: RefreshUsafOrderStatuses) {
    return this.usafService.refreshOrderStatuses(locationId).pipe(
      tap((res) => {
        ctx.patchState({ orderHistory: res })
      })
    )
  }

  @Action(CheckUsafStock)
  checkStock(ctx: StateContext<UsafStateModel>, { locationId, lines }: CheckUsafStock) {
    if (!lines?.length) return
    const partNumberToLineIds = new Map<string, string[]>()
    for (const line of lines) {
      const key = (line.partNumber ?? '').toLowerCase()
      const arr = partNumberToLineIds.get(key) ?? []
      arr.push(line.lineId)
      partNumberToLineIds.set(key, arr)
    }
    const req = {
      locationId,
      parts: lines.map((l) => ({
        lineCode: l.lineCode,
        partNumber: l.partNumber,
        qty: l.qty
      }))
    }
    return this.usafService.stockCheck(req).pipe(
      tap((res) => {
        // A failed USAF call comes back as empty placeholders — keep the current stock
        // view rather than wiping every line and the branch tabs.
        if (res?.failed) throw new UsafStockCheckFailedError()
        const branches = res?.branches ?? []
        const patch: Record<string, IUsafStockCheckResult> = {}
        for (const item of res?.items ?? []) {
          const ids = partNumberToLineIds.get((item.partNumber ?? '').toLowerCase())
          if (!ids) continue
          const availableByBranch: Record<string, number> = {}
          const rows = item.branches ?? []
          if (rows.length > 0) {
            for (const b of rows) {
              if (b.branchCode) availableByBranch[b.branchCode] = b.quantityAvailable ?? 0
            }
          } else {
            for (const b of branches) availableByBranch[b.code] = 0
          }
          const stock: IUsafStockCheckResult = {
            price: item.cost ?? 0,
            availableByBranch
          }
          for (const id of ids) patch[id] = stock
        }
        ctx.patchState({
          stockByLineId: { ...ctx.getState().stockByLineId, ...patch },
          branches
        })
      })
    )
  }

  @Action(ClearUsafStock)
  clearStock(ctx: StateContext<UsafStateModel>) {
    ctx.patchState({ stockByLineId: {}, branches: [] })
  }

  @Action(SetUsafStocks)
  setStocks(ctx: StateContext<UsafStateModel>, { stocks, branches }: SetUsafStocks) {
    const patch: Partial<UsafStateModel> = {
      stockByLineId: { ...ctx.getState().stockByLineId, ...stocks }
    }
    if (branches) patch.branches = branches
    ctx.patchState(patch)
  }

  @Action(SubmitUsafOrder)
  submitOrder(ctx: StateContext<UsafStateModel>, { order }: SubmitUsafOrder) {
    return this.usafService.submitOrder(order).pipe(
      tap((res) => {
        const current = ctx.getState().orderHistory ?? []
        ctx.patchState({
          lastSubmitResponse: res,
          orderHistory: [res, ...current]
        })
      })
    )
  }

  @Action(ClearUsafLocationData)
  clearLocationData(ctx: StateContext<UsafStateModel>) {
    ctx.patchState({
      orderHistory: undefined,
      stockByLineId: {},
      branches: [],
      lastSubmitResponse: undefined,
      tireSuggestions: []
    })
  }

  @Action(SearchUsafTireSuggestions, { cancelUncompleted: true })
  searchTireSuggestions(
    ctx: StateContext<UsafStateModel>,
    { query, locationId }: SearchUsafTireSuggestions
  ) {
    const filters: Record<string, unknown> = {
      partNumber: {
        key: 'partNumber',
        items: [{ operator: New_TableOperator.StartsWith, value: query }]
      }
    }
    if (locationId) {
      filters['locationId'] = {
        key: 'locationId',
        items: [{ operator: New_TableOperator.Equals, value: locationId }]
      }
    }
    return this.tiresService
      .getTireInventoryList({
        page: 1,
        sort: { active: 'partNumber', direction: 'asc' },
        filters: filters as never
      })
      .pipe(
        tap((res) => {
          ctx.patchState({ tireSuggestions: (res?.tires ?? []).slice(0, SUGGESTIONS_MAX) })
        }),
        catchError(() => {
          ctx.patchState({ tireSuggestions: [] })
          return of(null)
        })
      )
  }

  @Action(ClearUsafTireSuggestions)
  clearTireSuggestions(ctx: StateContext<UsafStateModel>) {
    ctx.patchState({ tireSuggestions: [] })
  }

  @Action(GetUsafOrdersList, { cancelUncompleted: true })
  getOrdersList(ctx: StateContext<UsafStateModel>, { info }: GetUsafOrdersList) {
    ctx.patchState({ ordersListResponse: undefined })
    return this.usafService.getOrdersList(info).pipe(
      tap((res) => {
        ctx.patchState({ ordersListResponse: res })
      })
    )
  }

  @Action(GetUsafOrdersOverview, { cancelUncompleted: true })
  getOrdersOverview(ctx: StateContext<UsafStateModel>, { info }: GetUsafOrdersOverview) {
    ctx.patchState({ ordersOverviewResponse: undefined })
    return this.usafService.getOrdersOverview(info).pipe(
      tap((res) => {
        ctx.patchState({ ordersOverviewResponse: res })
      })
    )
  }

  @Action(GetUsafOrderLinesList, { cancelUncompleted: true })
  getOrderLinesList(ctx: StateContext<UsafStateModel>, { info }: GetUsafOrderLinesList) {
    ctx.patchState({ orderLinesListResponse: undefined })
    return this.usafService.getOrderLinesList(info).pipe(
      tap((res) => {
        ctx.patchState({ orderLinesListResponse: res })
      })
    )
  }

  @Action(GetUsafInvoicesList, { cancelUncompleted: true })
  getInvoicesList(ctx: StateContext<UsafStateModel>, { info }: GetUsafInvoicesList) {
    ctx.patchState({ invoicesListResponse: undefined })
    return this.usafService.getInvoicesList(info).pipe(
      tap((res) => {
        ctx.patchState({ invoicesListResponse: res })
      })
    )
  }

  @Action(GetUsafAutoOrderAttemptsList, { cancelUncompleted: true })
  getAutoOrderAttemptsList(ctx: StateContext<UsafStateModel>, { info }: GetUsafAutoOrderAttemptsList) {
    ctx.patchState({ autoOrderAttemptsListResponse: undefined })
    return this.usafService.getAutoOrderAttemptsList(info).pipe(
      tap((res) => {
        ctx.patchState({ autoOrderAttemptsListResponse: res })
      })
    )
  }

  @Action(GetUsafAutoOrderOverview, { cancelUncompleted: true })
  getAutoOrderOverview(ctx: StateContext<UsafStateModel>, { info }: GetUsafAutoOrderOverview) {
    ctx.patchState({ autoOrderOverviewResponse: undefined })
    return this.usafService.getAutoOrderOverview(info).pipe(
      tap((res) => {
        ctx.patchState({ autoOrderOverviewResponse: res })
      })
    )
  }

  @Action(GetUsafLeftoversList, { cancelUncompleted: true })
  getLeftoversList(ctx: StateContext<UsafStateModel>, { info }: GetUsafLeftoversList) {
    ctx.patchState({ leftoversListResponse: undefined })
    return this.usafService.getLeftoversList(info).pipe(
      tap((res) => {
        ctx.patchState({ leftoversListResponse: res })
      })
    )
  }

  @Action(GetUsafOverdueOrdersList, { cancelUncompleted: true })
  getOverdueOrdersList(ctx: StateContext<UsafStateModel>, { info }: GetUsafOverdueOrdersList) {
    ctx.patchState({ overdueOrdersListResponse: undefined })
    return this.usafService.getOverdueOrdersList(info).pipe(
      tap((res) => {
        ctx.patchState({ overdueOrdersListResponse: res })
      })
    )
  }

  @Action(GetUsafWorkQueueSummary, { cancelUncompleted: true })
  getWorkQueueSummary(
    ctx: StateContext<UsafStateModel>,
    { leftovers, overdueOrders }: GetUsafWorkQueueSummary
  ) {
    ctx.patchState({ leftoversSummary: undefined, overdueOrdersSummary: undefined })
    return forkJoin([
      this.usafService.getLeftoversList(leftovers),
      this.usafService.getOverdueOrdersList(overdueOrders)
    ]).pipe(
      tap(([leftoversSummary, overdueOrdersSummary]) => {
        ctx.patchState({ leftoversSummary, overdueOrdersSummary })
      })
    )
  }

  @Action(GetUsafRefillLocations, { cancelUncompleted: true })
  getRefillLocations(ctx: StateContext<UsafStateModel>) {
    return this.usafService.getRefillLocations().pipe(
      tap((res) => {
        ctx.patchState({ refillLocations: res ?? [] })
      })
    )
  }

  @Action(GetUsafRefillPlan)
  getRefillPlan(ctx: StateContext<UsafStateModel>, { locationId }: GetUsafRefillPlan) {
    const plans = { ...ctx.getState().refillPlans }
    delete plans[locationId]
    ctx.patchState({ refillPlans: plans })
    return this.usafService.getRefillPlan(locationId).pipe(
      tap((res) => {
        ctx.patchState({ refillPlans: { ...ctx.getState().refillPlans, [locationId]: res } })
      })
    )
  }

  @Action(SubmitUsafRefill)
  submitRefill(ctx: StateContext<UsafStateModel>, { request }: SubmitUsafRefill) {
    return this.usafService.submitRefill(request).pipe(
      tap((res) => {
        ctx.patchState({
          refillSubmitResults: {
            ...ctx.getState().refillSubmitResults,
            [request.locationId]: res?.results ?? []
          }
        })
      })
    )
  }

  @Action(GetUsafOrderDetail, { cancelUncompleted: true })
  getOrderDetail(ctx: StateContext<UsafStateModel>, { orderId }: GetUsafOrderDetail) {
    ctx.patchState({ orderDetail: undefined })
    return this.usafService.getOrderDetail(orderId).pipe(
      tap((res) => {
        ctx.patchState({ orderDetail: res })
      })
    )
  }
}
